import type { ApiError } from '@techsofit/api-types';

/**
 * Thin fetch wrapper for the TechSoFit API, shared by every portal. Auth is
 * cookie-based (HttpOnly), so we only ever send `credentials: 'include'` and
 * never touch tokens in JS. For mutations we echo the `XSRF-TOKEN` cookie back
 * as the `X-XSRF-TOKEN` header (double-submit CSRF, spec §5). Non-2xx responses
 * throw {@link ApiClientError}.
 */

// Apps hit the API directly. Set VITE_API_BASE_URL per app (e.g. http://localhost:1100
// in dev, https://api.techsofit.com in prod). Falls back to a relative path if unset.
const API_ORIGIN = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') ?? '';
const BASE = `${API_ORIGIN}/api/v1`;

/**
 * Absolute URL for an API-served media path (e.g. {@code /api/v1/public/media/...}).
 * Images render in <img> tags, which bypass the api() client — so a bare relative path
 * would resolve against the app host (a gym subdomain that only serves the SPA), not the
 * API. Prefix it with the API origin so it hits the backend. Dev (no VITE_API_BASE_URL)
 * stays relative and rides the Vite /api proxy; absolute URLs (blob/SAS) pass through.
 */
export function mediaUrl(path: string | null | undefined): string | undefined {
  if (!path) return undefined;
  return /^https?:\/\//i.test(path) ? path : `${API_ORIGIN}${path}`;
}

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'ApiClientError';
    this.status = error.status;
    this.code = error.code;
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Ensure the double-submit CSRF cookie exists before a mutation. Right after
 * login/refresh the SPA can fire a mutation before the XSRF cookie has landed;
 * priming it here (and once more on a 403) makes mutations reliable.
 */
async function ensureXsrfToken(): Promise<string | null> {
  let token = readCookie('XSRF-TOKEN');
  if (!token) {
    await fetch(`${BASE}/auth/csrf`, { credentials: 'include' }).catch(() => undefined);
    token = readCookie('XSRF-TOKEN');
  }
  return token;
}

export interface ApiRequest {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Internal: already retried after a silent token refresh. */
  _retried?: boolean;
}

const SKIP_REFRESH = /\/(account-auth|member-auth|auth)\/(refresh|login|logout|request-code|request-otp|verify-code|verify-otp|password-login|support-enter)/;

let refreshInFlight: Promise<boolean> | null = null;

async function tryRefreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const xsrf = await ensureXsrfToken();
    for (const path of ['/account-auth/refresh', '/member-auth/refresh']) {
      const headers: Record<string, string> = {};
      if (xsrf) headers['X-XSRF-TOKEN'] = xsrf;
      const res = await fetch(`${BASE}${path}`, { method: 'POST', credentials: 'include', headers }).catch(
        () => null,
      );
      if (res?.ok) return true;
    }
    return false;
  })().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

export async function api<T>(path: string, req: ApiRequest = {}): Promise<T> {
  const method = req.method ?? 'GET';
  const mutation = method !== 'GET';

  const send = async (xsrf: string | null) => {
    const headers: Record<string, string> = {};
    const init: RequestInit = { method, credentials: 'include', headers };
    if (req.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(req.body);
    }
    if (xsrf) headers['X-XSRF-TOKEN'] = xsrf;
    return fetch(`${BASE}${path}`, init);
  };

  let res = await send(mutation ? await ensureXsrfToken() : null);

  // A 403 on a mutation is almost always a stale/missing CSRF token — re-prime
  // and retry once before surfacing it. (CSRF is rejected before the controller,
  // so the request had no side effects.)
  if (res.status === 403 && mutation) {
    await fetch(`${BASE}/auth/csrf`, { credentials: 'include' }).catch(() => undefined);
    res = await send(readCookie('XSRF-TOKEN'));
  }

  if (res.status === 401 && !req._retried && !SKIP_REFRESH.test(path)) {
    const refreshed = await tryRefreshSession();
    if (refreshed) {
      return api<T>(path, { ...req, _retried: true });
    }
  }

  if (res.status === 204) return undefined as T;

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await res.json() : undefined;

  if (!res.ok) {
    throw new ApiClientError(
      (payload as ApiError) ?? { status: res.status, code: 'error', message: res.statusText },
    );
  }
  return payload as T;
}

/**
 * Multipart upload (e.g. a progress photo). Mirrors {@link api}'s auth/CSRF handling
 * but sends FormData — we must NOT set Content-Type so the browser adds the multipart
 * boundary itself. Re-primes the XSRF token and retries once on a 403.
 */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const send = (xsrf: string | null) => {
    const headers: Record<string, string> = {};
    if (xsrf) headers['X-XSRF-TOKEN'] = xsrf;
    return fetch(`${BASE}${path}`, { method: 'POST', credentials: 'include', headers, body: form });
  };

  let res = await send(await ensureXsrfToken());
  if (res.status === 403) {
    await fetch(`${BASE}/auth/csrf`, { credentials: 'include' }).catch(() => undefined);
    res = await send(readCookie('XSRF-TOKEN'));
  }

  if (res.status === 401) {
    const refreshed = await tryRefreshSession();
    if (refreshed) {
      res = await send(await ensureXsrfToken());
    }
  }

  if (res.status === 204) return undefined as T;
  const isJson = res.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await res.json() : undefined;
  if (!res.ok) {
    throw new ApiClientError(
      (payload as ApiError) ?? { status: res.status, code: 'error', message: res.statusText },
    );
  }
  return payload as T;
}
